import { Node, mergeAttributes } from '@tiptap/react'
import { getDisplayNumbers } from './sourceRegistryPlugin'

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    citationNode: {
      insertCitation: (attrs: {
        /** Legacy: per-claim id. Either `claimId` or `attachmentId` must be set. */
        claimId?: string
        /** PR4: per-attachment id (one source × clause-run × sentence). */
        attachmentId?: string
        /** JSON array of source UUIDs, e.g. '["uuid-1","uuid-2"]'.
         *  In the new attachment flow this is a single-element array. */
        sourceUuids?: string
        /** JSON array of ref IDs (legacy, kept for backward compat) */
        indices?: string
        /** JSON array of 0-based claim indices this citation covers (API flow only) */
        claimIndices?: string | null
        /** D6: hash of containing sentence text (Layer 2 staleness primitive). */
        sentenceHash?: string | null
      }) => ReturnType
    }
  }
}

/**
 * Glyph used for citation chips on audited sentences that produced no
 * supporting evidence (every source returned not_relevant). Rendered
 * instead of a number; the chip is styled via `.citation-no-source`.
 */
const NO_SOURCE_GLYPH = 'ⓘ' // ⓘ

/** True when a citation's sourceUuids attribute parses to an empty array. */
export function isNoSourceCitation(sourceUuids: unknown): boolean {
  if (typeof sourceUuids !== 'string') return false
  try {
    const parsed = JSON.parse(sourceUuids)
    return Array.isArray(parsed) && parsed.length === 0
  } catch {
    return false
  }
}

/**
 * Format a sorted array of citation indices into range notation.
 * [1,2,3] → "1-3"
 * [1,4,5,6,8] → "1,4-6,8"
 */
export function formatCitationText(indices: number[]): string {
  if (indices.length === 0) return ''
  const sorted = [...new Set(indices)].sort((a, b) => a - b)
  const parts: string[] = []
  let i = 0

  while (i < sorted.length) {
    const start = sorted[i]
    // Walk forward while consecutive
    while (i < sorted.length - 1 && sorted[i + 1] === sorted[i] + 1) {
      i++
    }
    const end = sorted[i]
    if (end - start >= 2) {
      parts.push(`${start}-${end}`)
    } else if (end - start === 1) {
      parts.push(`${start},${end}`)
    } else {
      parts.push(String(start))
    }
    i++
  }

  return parts.join(',')
}

export const CitationNode = Node.create({
  name: 'citation',

  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      claimId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-claim-id'),
        renderHTML: (attrs) => ({ 'data-claim-id': attrs.claimId }),
      },
      attachmentId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-attachment-id'),
        renderHTML: (attrs) =>
          attrs.attachmentId
            ? { 'data-attachment-id': attrs.attachmentId }
            : {},
      },
      sourceUuids: {
        default: '[]',
        parseHTML: (el) => el.getAttribute('data-source-uuids') ?? '[]',
        renderHTML: (attrs) => ({ 'data-source-uuids': attrs.sourceUuids }),
      },
      indices: {
        default: '[]',
        parseHTML: (el) => el.getAttribute('data-citation-indices') ?? '[]',
        renderHTML: (attrs) => ({ 'data-citation-indices': attrs.indices }),
      },
      claimIndices: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-claim-indices') ?? null,
        renderHTML: (attrs) =>
          attrs.claimIndices !== null && attrs.claimIndices !== undefined
            ? { 'data-claim-indices': attrs.claimIndices }
            : {},
      },
      // D6: hash of the sentence text containing this citation. Backend writes
      // it; frontend stores it. Layer 2 per-claim staleness detection will
      // consume it later — Tier 1 just round-trips through parse/render.
      sentenceHash: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-sentence-hash') ?? null,
        renderHTML: (attrs) =>
          attrs.sentenceHash
            ? { 'data-sentence-hash': attrs.sentenceHash }
            : {},
      },
    }
  },

  parseHTML() {
    return [{ tag: 'sup[data-citation]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    // Late-binding: display numbers are computed by the Source Registry plugin.
    // At renderHTML time we don't have access to plugin state, so we fall back
    // to legacy indices if available, or show a placeholder.
    let displayText = '?'
    let extraClass = ''

    // No-source marker — backend emitted a citation for an audited sentence
    // with zero supporting evidence. Render as ⓘ chip, never as a number.
    if (isNoSourceCitation(node.attrs.sourceUuids)) {
      displayText = NO_SOURCE_GLYPH
      extraClass = ' citation-no-source'
    } else {
      // Try legacy indices first (backward compat)
      try {
        const raw = JSON.parse(node.attrs.indices)
        if (Array.isArray(raw) && raw.length > 0) {
          displayText = formatCitationText(raw)
        }
      } catch { /* empty */ }
    }

    return [
      'sup',
      mergeAttributes(HTMLAttributes, {
        'data-citation': '',
        class: 'citation-sup' + extraClass,
      }),
      displayText,
    ]
  },

  addNodeView() {
    return ({ node, editor }) => {
      const dom = document.createElement('sup')
      dom.setAttribute('data-citation', '')
      dom.classList.add('citation-sup')

      function render(n: typeof node) {
        if (n.attrs.claimId) dom.setAttribute('data-claim-id', n.attrs.claimId)
        if (n.attrs.attachmentId) dom.setAttribute('data-attachment-id', n.attrs.attachmentId)
        if (n.attrs.sourceUuids) dom.setAttribute('data-source-uuids', n.attrs.sourceUuids)
        if (n.attrs.indices) dom.setAttribute('data-citation-indices', n.attrs.indices)
        if (n.attrs.claimIndices != null) dom.setAttribute('data-claim-indices', n.attrs.claimIndices)
        if (n.attrs.sentenceHash) dom.setAttribute('data-sentence-hash', n.attrs.sentenceHash)

        // 0. No-source marker — audited sentence with no supporting evidence.
        if (isNoSourceCitation(n.attrs.sourceUuids)) {
          dom.classList.add('citation-no-source')
          dom.setAttribute('title', 'No supporting evidence found in uploaded sources')
          dom.textContent = NO_SOURCE_GLYPH
          return
        }
        dom.classList.remove('citation-no-source')
        dom.removeAttribute('title')

        // 1. Try Source Registry (new flow)
        const nums = getDisplayNumbers(editor.state, n.attrs.sourceUuids ?? '[]')
        if (nums.length > 0) { dom.textContent = formatCitationText(nums); return }

        // 2. Fallback: legacy indices
        try {
          const raw = JSON.parse(n.attrs.indices)
          if (Array.isArray(raw) && raw.length > 0) { dom.textContent = formatCitationText(raw); return }
        } catch { /* empty */ }

        // 3. Last resort
        dom.textContent = '?'
      }

      render(node)

      return {
        dom,
        update(updatedNode) {
          if (updatedNode.type.name !== 'citation') return false
          render(updatedNode)
          return true
        },
        ignoreMutation() { return true },
      }
    }
  },

  addCommands() {
    return {
      insertCitation:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs,
          }),
    }
  },
})
